import { useMemo, useContext, useEffect, useRef } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { Text, OrderedList, ListItem, Box } from '@chakra-ui/react';

import Card from 'components/card/Card';

import TextFieldInput from 'components/fields/auto/TextFieldInput';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import capitalizeFirstLetterInAllWords from 'helpers/capitalizeFirstLetterInAllWords';
import getValidationMessage from 'helpers/getValidationMessage';
import requiresPropertyBinderNumber from 'helpers/requiresPropertyBinderNumber';

import Message from 'components/fields/auto/Message';
import { Validation006Resolver } from 'components/fields/auto/resolvers';

import {
  addOrUpdateRemark,
  addRemark,
  removeRemark,
  setNotesToUnderwriter,
} from 'reducers/autoPersonalFormReducer';

import { FILE_TYPE } from 'variables/constants';
import {
  SECTIONS,
  MULTI_LINE_DISCOUNT_VALIDATION_ID,
} from 'views/auto_form/constants';

import type { APIRemark } from 'types/api.types';

const Remarks = () => {
  const dispatch = useAppDispatch();
  const isValidationResolversEnabled = useFeatureFlag(
    'enable-validation-resolvers',
  );

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const requiresBinder = requiresPropertyBinderNumber(validations);

  const validation006Message = getValidationMessage(validations, '006');
  const validation060Message = getValidationMessage(validations, '060');
  const validation152Message = getValidationMessage(validations, '152');
  const marketApprovalValidations = [
    { validationId: '006', message: validation006Message },
    { validationId: '060', message: validation060Message },
  ].filter(
    (validation): validation is { validationId: string; message: string } =>
      !!validation.message,
  );

  const remarksWithText = useAppSelector((state) =>
    state.autoPersonalForm.remarks.filter(
      (r) =>
        r.text &&
        r.text.trim().length > 0 &&
        !(r.validation_key === '133b' && r.status === 'failed') &&
        r.custom_data?.type !== 'market_approval',
    ),
  );
  const vehicles = useAppSelector(
    (state) => state.autoPersonalForm.data.vehicles,
  );
  const files = useAppSelector((state) => state.autoPersonalForm.files);
  const ratingInformationDiscounts = useAppSelector(
    (state) => state.autoPersonalForm.data.rating_information_discounts,
  );
  const additionalRemark = useAppSelector((state) =>
    state.autoPersonalForm.remarks.find(
      (r) => r.validation_key === 'additional',
    ),
  );
  const notesToUnderwriter = useAppSelector(
    (state) => state.autoPersonalForm.data?.notes_to_underwriter || '',
  );

  const hasGroupDiscount = useMemo(() => {
    return ratingInformationDiscounts.some((discount) => {
      return discount.discounts.some((subDiscount) => {
        return subDiscount.discount_description
          .toLowerCase()
          .includes('group discount included');
      });
    });
  }, [ratingInformationDiscounts]);

  const hasValidation035 = !!getValidationMessage(validations, '035');
  const hasValidation090a = !!getValidationMessage(validations, '090a');
  const hasValidation090b = !!getValidationMessage(validations, '090b');
  const vehicleDeclarationText = useMemo(() => {
    const vehicleDescriptions = vehicles
      .map((vehicle) => {
        const year = vehicle.model_year || '';
        const make = vehicle.make || '';
        const model = vehicle.model || '';
        const vin = vehicle.vin || '';
        const description = `${year} ${make} ${model}`.trim();
        if (description && vin) return `${description} (VIN: ${vin})`;
        if (description) return description;
        if (vin) return `VIN: ${vin}`;
        return '';
      })
      .filter(Boolean);

    return vehicleDescriptions.length > 0
      ? vehicleDescriptions.join('; ')
      : 'the insured vehicle(s)';
  }, [vehicles]);

  const hasVehiclePhotos = useMemo(() => {
    return files.some((file) => {
      if (file.validation_key !== '092') return false;
      if (!file.ai_result) return false;

      try {
        const description = (file.ai_result || '').toLowerCase();
        return !description.includes('bill of sale');
      } catch {
        return false;
      }
    });
  }, [files]);

  const isLessorDefined = (lessor: any) => {
    return (
      lessor &&
      (lessor.name ||
        lessor.address ||
        lessor.city ||
        lessor.province_code ||
        lessor.postal_code)
    );
  };

  useEffect(() => {
    let firstLessorVehicleFound = false;

    vehicles.forEach((vehicle, index) => {
      if (isLessorDefined(vehicle.lessor)) {
        if (!firstLessorVehicleFound) {
          firstLessorVehicleFound = true;

          dispatch(
            addOrUpdateRemark({
              validation_key: 'lessor',
              item_key: `vehicle-lessor-${index + 1}`,
              data: {
                text: '',
              },
            }),
          );
        } else {
          const lessorRemark = `Vehicle ${index + 1} Lessor - Name: ${
            vehicle.lessor.name || 'N/A'
          } - Address: ${vehicle.lessor.address || 'N/A'} - City: ${
            vehicle.lessor.city || 'N/A'
          } - Province: ${
            vehicle.lessor.province_code || 'N/A'
          } - Postal Code: ${vehicle.lessor.postal_code || 'N/A'}`;

          dispatch(
            addOrUpdateRemark({
              validation_key: 'lessor',
              item_key: `lessor-vehicle-${index + 1}`,
              data: {
                text: lessorRemark,
              },
            }),
          );
        }
      } else {
        dispatch(
          removeRemark({
            validation_key: 'lessor',
            item_key: `lessor-vehicle-${index + 1}`,
          }),
        );
      }
    });
  }, [vehicles, dispatch]);

  useEffect(() => {
    if (hasValidation035) {
      dispatch(
        addRemark({
          validation_key: '035',
          item_key: 'declaration',
          text: `I confirm that the photographs submitted with the application for insurance are true and accurate representations of ${vehicleDeclarationText} and reflect its condition at the time of submission.`,
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.REMARKS,
        }),
      );
    } else {
      dispatch(
        removeRemark({
          validation_key: '035',
          item_key: 'declaration',
        }),
      );
    }
  }, [dispatch, hasValidation035, vehicleDeclarationText]);

  useEffect(() => {
    if (hasValidation090a) {
      dispatch(
        addRemark({
          validation_key: '090',
          item_key: 'declaration',
          text: `I confirm that the photographs submitted with the application for insurance are true and accurate representations of ${vehicleDeclarationText} and reflect its condition at the time of submission.`,
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.REMARKS,
        }),
      );
      return;
    }

    dispatch(
      removeRemark({
        validation_key: '090',
        item_key: 'declaration',
      }),
    );
  }, [dispatch, hasValidation090a, vehicleDeclarationText]);

  useEffect(() => {
    if (hasValidation090b) {
      dispatch(
        addRemark({
          validation_key: '090b',
          item_key: 'declaration',
          text: `I confirm that the photographs submitted with the application for insurance are true and accurate representations of ${vehicleDeclarationText} and reflect its condition at the time of submission.`,
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.REMARKS,
        }),
      );
      return;
    }

    dispatch(
      removeRemark({
        validation_key: '090b',
        item_key: 'declaration',
      }),
    );
  }, [dispatch, hasValidation090b, vehicleDeclarationText]);

  useEffect(() => {
    if (hasVehiclePhotos) {
      vehicles.forEach((vehicle, index) => {
        const vehicleDesc = `${vehicle.model_year} ${vehicle.make} ${vehicle.model}, VIN: ${vehicle.vin}`;
        dispatch(
          addOrUpdateRemark({
            validation_key: '092-declaration',
            item_key: `vehicle-photo-${index + 1}`,
            data: {
              text: `I confirm that the photographs submitted with the application for insurance are true and accurate representations of the ${vehicleDesc} and reflect its condition at the time of submission.`,
              status: 'approved',
            },
          }),
        );
      });
    } else {
      vehicles.forEach((_, index) => {
        dispatch(
          removeRemark({
            validation_key: '092-declaration',
            item_key: `vehicle-photo-${index + 1}`,
          }),
        );
      });
    }
  }, [hasVehiclePhotos, vehicles, dispatch]);

  useEffect(() => {
    vehicles.forEach((vehicle, index) => {
      const validation153bMessage = getValidationMessage(
        validations,
        '153b',
        index + 1,
      );
      if (validation153bMessage) {
        const vehicleDesc = `${vehicle.model_year} ${vehicle.make} ${vehicle.model}, VIN: ${vehicle.vin}`;
        dispatch(
          addOrUpdateRemark({
            validation_key: '153b',
            item_key: `vehicle-${index + 1}`,
            data: {
              text: `I confirm that the photographs submitted with the application for insurance are true and accurate representations of the ${vehicleDesc} and reflect its condition at the time of submission.`,
              status: 'approved',
            },
          }),
        );
      } else {
        dispatch(
          removeRemark({
            validation_key: '153b',
            item_key: `vehicle-${index + 1}`,
          }),
        );
      }
    });
  }, [vehicles, validations, dispatch]);

  const allQ1Yes = vehicles.every(
    (vehicle) => vehicle.accident_benefits_questions?.question_1 === true,
  );
  const allQ2Yes = vehicles.every(
    (vehicle) => vehicle.accident_benefits_questions?.question_2 === true,
  );
  const allQ3Yes = vehicles.every(
    (vehicle) => vehicle.accident_benefits_questions?.question_3 === true,
  );

  const hasMountedAbQuestionsEffect = useRef(false);

  useEffect(() => {
    if (!hasMountedAbQuestionsEffect.current) {
      hasMountedAbQuestionsEffect.current = true;
      return;
    }

    dispatch(
      removeRemark({
        validation_key: 'accident_benefits_q1',
        item_key: 'global',
      }),
    );
    dispatch(
      removeRemark({
        validation_key: 'accident_benefits_q2',
        item_key: 'global',
      }),
    );
    dispatch(
      removeRemark({
        validation_key: 'accident_benefits_q3',
        item_key: 'global',
      }),
    );

    if (allQ1Yes) {
      dispatch(
        addRemark({
          validation_key: 'accident_benefits_q1',
          item_key: 'global',
          text: 'I acknowledge that my broker has explained the Accident Benefits described available to me, including which benefits are included/mandatory and which are optional. I have had the opportunity to ask questions before making my selections and understand that optional benefits not selected will not provide coverage.',
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.REMARKS,
        }),
      );
    }

    if (allQ2Yes) {
      dispatch(
        addRemark({
          validation_key: 'accident_benefits_q2',
          item_key: 'global',
          text: 'I confirm that the Accident Benefits selected on my Coverage Summary and Application for Automobile Insurance accurately reflect my informed choices. I understand these selections apply to all drivers listed on the policy and that any optional benefit not selected will not provide coverage.',
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.REMARKS,
        }),
      );
    }

    if (allQ3Yes) {
      dispatch(
        addRemark({
          validation_key: 'accident_benefits_q3',
          item_key: 'global',
          text: 'I understand and my broker has informed me that as of July 1, 2026, Ontario automobile insurance reforms changed how certain accident-related expenses are coordinated, and that my automobile insurer will generally be the first payor for most medical, rehabilitation, and attendant care expenses (excluding prescription medications).',
          status: 'approved',
          text_validator_log_id: null,
          ai_result: '',
          section: SECTIONS.REMARKS,
        }),
      );
    }
  }, [allQ1Yes, allQ2Yes, allQ3Yes, dispatch]);

  const getRemarkText = (remark: APIRemark) => {
    const validation = validations.find((v) =>
      remark.validation_key === '133b'
        ? v.validation_id === remark.validation_key
        : Number(v.validation_id) === Number(remark.validation_key),
    );
    const validation_name =
      remark.validation_key === '133b'
        ? 'License and Insurance Experience'
        : validation?.validation_name ||
          capitalizeFirstLetterInAllWords(
            remark.validation_key.replaceAll('_', ' '),
          );

    if (remark.item_key.startsWith('vehicle-')) {
      const vehicle_nr = remark.item_key.replace('vehicle-', '');
      return `${validation_name} (vehicle ${vehicle_nr}) Remarks - ${remark.text}`;
    }

    if (remark.item_key.startsWith('driver-')) {
      const driver_nr = remark.item_key.replace('driver-', '');
      return `${validation_name} (driver ${driver_nr}) Remarks - ${remark.text}`;
    }

    return `${validation_name} Remarks - ${remark.text}`;
  };

  const onGlobalRemarksChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(
      addOrUpdateRemark({
        validation_key: name,
        item_key: 'global',
        data: {
          text: value,
        },
      }),
    );
  };

  const onNotesToUnderwriterChange = (event: any) => {
    dispatch(setNotesToUnderwriter(event.target.value));
  };

  return (
    <Card mb="20px" id="sectionRemarks">
      <Text fontWeight="bold" fontSize="xl" mb="20px">
        Remarks
      </Text>

      {getValidationMessage(validations, '077') && (
        <Box mb="20px">
          {isValidationResolversEnabled ? (
            <Validation006Resolver
              section={SECTIONS.REMARKS}
              validationId="077"
            />
          ) : (
            <Message validationId="077" section={SECTIONS.REMARKS} />
          )}
        </Box>
      )}

      {getValidationMessage(validations, '087') && (
        <Box mb="20px">
          <Message validationId="087" section={SECTIONS.REMARKS} />
        </Box>
      )}

      {marketApprovalValidations.map(({ validationId, message }) => (
        <Box mb="20px" key={validationId}>
          {isValidationResolversEnabled ? (
            <Validation006Resolver
              section={SECTIONS.REMARKS}
              validationId={validationId}
              forceShow
              customAlertMessage={message}
            />
          ) : (
            <Message
              validationId={validationId}
              section={SECTIONS.REMARKS}
              manual={{
                show: true,
                message,
              }}
              file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
            />
          )}
        </Box>
      ))}

      {validation152Message && (
        <Box mb="20px">
          {isValidationResolversEnabled ? (
            <Validation006Resolver
              section={SECTIONS.REMARKS}
              validationId="152"
              forceShow
              customAlertMessage={validation152Message}
              emailOnly
            />
          ) : (
            <Message
              validationId="152"
              section={SECTIONS.REMARKS}
              manual={{
                show: true,
                message: validation152Message,
              }}
              file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
            />
          )}
        </Box>
      )}

      {getValidationMessage(validations, '079') && (
        <Box mb="20px">
          <Message validationId="079" section={SECTIONS.REMARKS} />
        </Box>
      )}

      {!requiresBinder &&
        getValidationMessage(
          validations,
          MULTI_LINE_DISCOUNT_VALIDATION_ID,
        ) && (
          <Box mb="20px">
            <Message
              validationId={MULTI_LINE_DISCOUNT_VALIDATION_ID}
              section={SECTIONS.REMARKS}
            />
          </Box>
        )}

      {getValidationMessage(validations, '035') && (
        <Box mb="20px">
          <Message
            validationId="035"
            section={SECTIONS.REMARKS}
            hideRemarks
            file={FILE_TYPE.TYPE_035}
          />
        </Box>
      )}

      {getValidationMessage(validations, '048') && (
        <Box mb="20px">
          {isValidationResolversEnabled ? (
            <Validation006Resolver
              section={SECTIONS.REMARKS}
              validationId="048"
              approvalNumberRequired
            />
          ) : (
            <Message
              validationId="048"
              section={SECTIONS.REMARKS}
              file={FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO}
            />
          )}
        </Box>
      )}

      {hasGroupDiscount && (
        <Box mb="20px">
          <Message
            section={SECTIONS.REMARKS}
            validationId="group_discount"
            manual={{
              message:
                'Group Discount found on quote. Please enter the employee number in the remarks.',
              show: true,
            }}
          />
        </Box>
      )}

      {!remarksWithText.length && <div>Empty...</div>}

      <OrderedList spacing="1">
        {remarksWithText.map((remark) => {
          return (
            <ListItem key={`${remark.validation_key}-${remark.item_key}`}>
              {getRemarkText(remark)}
            </ListItem>
          );
        })}
      </OrderedList>

      <Box mt="20px">
        <TextFieldInput
          section={SECTIONS.REMARKS}
          label="Additional Remarks"
          value={additionalRemark?.text || ''}
          onChange={onGlobalRemarksChange}
          name="additional"
          maxH="auto"
        />
      </Box>

      <Box mt="20px">
        <TextFieldInput
          section={SECTIONS.REMARKS}
          label="Notes for BIG Underwriting"
          value={notesToUnderwriter}
          onChange={onNotesToUnderwriterChange}
          name="notes_to_underwriter"
          maxH="auto"
        />
      </Box>
    </Card>
  );
};

export default Remarks;
